import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { AuthLayout } from '@/pages/Login';
import { Logo } from '@/components/Logo';

export default function Signup() {
  const nav = useNavigate();
  const { setSession } = useAuthStore();
  const [form, setForm] = React.useState({ email: '', password: '', full_name: '', org_name: '' });
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/signup', form);
      setSession({ token: data.access_token, user: data.user });
      toast.success('Workspace created');
      nav('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-md">
        <Logo />
        <h1 className="mt-8 text-2xl font-semibold tracking-tight" data-testid="signup-title">Create your workspace</h1>
        <p className="mt-1 text-sm text-[hsl(var(--text-3))]">Trial starts at 10 agents. No card required.</p>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="signup-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="full_name">Full name</Label>
              <Input id="full_name" required placeholder="Jane Operator" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="signup-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org_name">Workspace name</Label>
              <Input id="org_name" required placeholder="Acme Labs" value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="signup-org-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Work email</Label>
            <Input id="email" type="email" required autoComplete="email" placeholder="you@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required autoComplete="new-password" placeholder="Min 8 characters" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signup-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full" data-testid="signup-submit-button">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create workspace
          </Button>
          <p className="text-sm text-[hsl(var(--text-3))]">
            Already have an account? <Link to="/login" className="text-[hsl(var(--primary))] hover:underline" data-testid="signup-login-link">Sign in</Link>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
}
